import type { FeaturesContent } from "../i18n/types";

/** Mockup views — structural, zipped with the screenshot content in the
 * dictionary. */
const VIEWS = ["inference", "compute", "access"] as const;

export function renderFeatures(t: FeaturesContent): HTMLElement {
  const root = document.createElement("section");
  root.className = "section platform-showcase";
  root.id = "features";
  root.innerHTML = `
    <div class="page">
      <div class="section-head section-head--center" data-reveal>
        <h2 class="section__title">${t.title}</h2>
        <p class="section__lead">
          ${t.lead}
        </p>
      </div>
      <div class="platform-showcase__grid">
        ${t.screenshots.map((screenshot, index) => `
          <figure class="platform-shot-card" data-reveal style="--reveal-delay:${index * 80}ms">
            <div class="platform-shot" aria-label="${screenshot.frameAriaLabel}">
              <div class="platform-shot__bar">
                <span class="platform-shot__dots" aria-hidden="true"><i></i><i></i><i></i></span>
                <span>ucloud / ${VIEWS[index]}</span>
                <span>•••</span>
              </div>
              ${renderScreenshot(VIEWS[index], screenshot.mockupAriaLabel)}
            </div>
            <figcaption>
              <h3>${screenshot.title}</h3>
              <p>${screenshot.description}</p>
            </figcaption>
          </figure>
        `).join("")}
      </div>
    </div>
  `;
  return root;
}

function renderScreenshot(view: (typeof VIEWS)[number], ariaLabel: string): string {
  switch (view) {
    case "inference":
      return `
        <svg class="platform-shot__image" viewBox="0 0 480 276" role="img" aria-label="${ariaLabel}">
          <rect width="480" height="276" fill="var(--gray-90)"/>
          <path d="M136 0v276M0 50h480M0 105h480M0 160h480M0 215h480" stroke="var(--gray-80)"/>
          <rect x="22" y="20" width="92" height="10" rx="2" fill="var(--gray-70)"/>
          <rect x="22" y="70" width="82" height="7" rx="2" fill="var(--gray-70)"/>
          <rect x="22" y="88" width="98" height="7" rx="2" fill="var(--gray-80)"/>
          <rect x="22" y="123" width="78" height="7" rx="2" fill="var(--gray-70)"/>
          <rect x="22" y="141" width="108" height="7" rx="2" fill="var(--gray-80)"/>
          <rect x="164" y="20" width="104" height="9" rx="2" fill="var(--gray-60)"/>
          <rect x="382" y="18" width="70" height="13" rx="2" fill="var(--blue-80)"/>
          <path d="M164 76h268M164 94h224M164 112h245" stroke="var(--gray-60)" stroke-width="5" stroke-linecap="round"/>
          <path d="M164 151h212M164 169h252M164 187h188" stroke="var(--gray-70)" stroke-width="5" stroke-linecap="round"/>
          <circle cx="420" cy="151" r="7" fill="var(--blue-40)"/>
          <path d="M164 236 C210 219 234 232 270 215 S333 226 366 201 S407 211 452 182" fill="none" stroke="var(--blue-40)" stroke-width="3"/>
        </svg>
      `;
    case "compute":
      return `
        <svg class="platform-shot__image" viewBox="0 0 480 276" role="img" aria-label="${ariaLabel}">
          <rect width="480" height="276" fill="var(--gray-90)"/>
          <path d="M0 52h480M0 224h480M148 52v172M324 52v172" stroke="var(--gray-80)"/>
          <rect x="22" y="19" width="85" height="11" rx="2" fill="var(--gray-60)"/>
          <rect x="346" y="17" width="108" height="15" rx="2" fill="var(--blue-80)"/>
          <rect x="22" y="76" width="100" height="9" rx="2" fill="var(--gray-70)"/>
          <rect x="22" y="97" width="75" height="7" rx="2" fill="var(--gray-80)"/>
          <rect x="22" y="118" width="92" height="7" rx="2" fill="var(--gray-80)"/>
          <rect x="174" y="76" width="126" height="10" rx="2" fill="var(--gray-60)"/>
          <rect x="174" y="101" width="108" height="7" rx="2" fill="var(--gray-70)"/>
          <rect x="174" y="118" width="84" height="7" rx="2" fill="var(--gray-80)"/>
          <circle cx="199" cy="161" r="16" fill="var(--blue-80)" stroke="var(--blue-40)"/>
          <circle cx="246" cy="161" r="16" fill="var(--blue-80)" stroke="var(--blue-40)"/>
          <circle cx="293" cy="161" r="16" fill="var(--blue-80)" stroke="var(--blue-40)"/>
          <path d="M215 161h15M262 161h15" stroke="var(--blue-40)" stroke-width="2"/>
          <rect x="348" y="77" width="91" height="8" rx="2" fill="var(--gray-60)"/>
          <rect x="348" y="100" width="72" height="8" rx="2" fill="var(--gray-70)"/>
          <path d="M348 149h91M348 165h71M348 181h81" stroke="var(--gray-70)" stroke-width="6" stroke-linecap="round"/>
        </svg>
      `;
    case "access":
      return `
          <svg class="platform-shot__image" viewBox="0 0 480 276" role="img" aria-label="${ariaLabel}">
          <rect width="480" height="276" fill="var(--gray-90)"/>
          <path d="M0 52h480M148 52v224" stroke="var(--gray-80)"/>
          <rect x="22" y="19" width="85" height="11" rx="2" fill="var(--gray-60)"/>
          <rect x="346" y="17" width="108" height="15" rx="2" fill="var(--blue-80)"/>
          <rect x="22" y="76" width="100" height="9" rx="2" fill="var(--gray-70)"/>
          <rect x="22" y="97" width="75" height="7" rx="2" fill="var(--gray-80)"/>
          <rect x="22" y="118" width="92" height="7" rx="2" fill="var(--gray-80)"/>
          <rect x="22" y="139" width="70" height="7" rx="2" fill="var(--gray-80)"/>
          <path d="M300 108 L202 150 M300 108 L300 150 M300 108 L398 150" stroke="var(--gray-70)" stroke-width="2"/>
          <rect x="225" y="72" width="150" height="36" rx="8" fill="var(--blue-80)"/>
          <circle cx="247" cy="90" r="9" fill="var(--blue-40)"/>
          <rect x="264" y="86" width="92" height="8" rx="2" fill="var(--blue-20)"/>
          <rect x="158" y="150" width="88" height="30" rx="6" fill="var(--gray-80)"/>
          <rect x="168" y="159" width="12" height="12" rx="3" fill="var(--blue-40)"/>
          <rect x="186" y="162" width="48" height="7" rx="2" fill="var(--gray-60)"/>
          <rect x="256" y="150" width="88" height="30" rx="6" fill="var(--gray-80)"/>
          <rect x="266" y="159" width="12" height="12" rx="3" fill="var(--blue-40)"/>
          <rect x="284" y="162" width="48" height="7" rx="2" fill="var(--gray-60)"/>
          <rect x="354" y="150" width="88" height="30" rx="6" fill="var(--gray-80)"/>
          <rect x="364" y="159" width="12" height="12" rx="3" fill="var(--blue-40)"/>
          <rect x="382" y="162" width="48" height="7" rx="2" fill="var(--gray-60)"/>
          <rect x="195" y="207" width="190" height="30" rx="6" fill="none" stroke="var(--gray-70)" stroke-dasharray="4 4"/>
          <rect x="207" y="216" width="12" height="12" rx="3" fill="var(--gray-70)"/>
          <rect x="225" y="219" width="112" height="7" rx="2" fill="var(--gray-70)"/>
          <path d="M197 233 L383 211" stroke="var(--red-70)" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
      `;
  }
}
